import { Signal } from '@preact/signals';
import type { StorageDefinition } from 'utils/types';

export const AB_TEST_COOKIE = 'sbazarAB';
export const SID_COOKIE = 'sid';
export const CORRELATION_COOKIE = 'correlation_id';
export const CSRF_COOKIE = 'csrf';
export const BLOCKED_COOKIES_CMP = [AB_TEST_COOKIE];
export const ALLOW_TESTS_COOKIES = 'allowTests';
export const FILTERS_PANEL_OPENED_COOKIE = 'filtersPanelOpened';

/**
 * Pomocná cookie slouží pro ověření, zda uživatel souhlasí s účelem 1 – ukládání a čtení cookies z koncového zařízení.
 */
export const SZNCMPONE_COOKIE = 'szncmpone';
export const SZNCMPONE_COOKIE_AGREED_VALUE = '1';

export const SBAZAR_COOKIES: StorageDefinition[] = [
	{
		name: AB_TEST_COOKIE,
		description: 'Cookie pro AB testování různých variant rozhraní a funkcionalit',
		type: 'Analytická',
		expiration: '7 dní',
	},
	{
		name: CORRELATION_COOKIE,
		description: 'Jedinečný identifikátor požadavku pro sledování a diagnostiku',
		type: 'Analytická',
		expiration: 'Do zavření prohlížeče',
	},
	{
		name: CSRF_COOKIE,
		description: 'Token pro ochranu proti útokům CSRF (Cross-Site Request Forgery)',
		type: 'Technická',
		expiration: '1 rok',
	},
	{
		name: ALLOW_TESTS_COOKIES,
		description: 'Povolení testovacích funkcí a debugování pro vývojáře',
		type: 'Preferenční',
		expiration: '7 dní',
	},
	{
		name: FILTERS_PANEL_OPENED_COOKIE,
		description: 'Stav rozbalovacího panelu s filtry pro vyhledávání',
		type: 'Preferenční',
		expiration: '7 dní',
	},
	{
		name: SZNCMPONE_COOKIE,
		description: 'Souhlas uživatele s ukládáním a čtením cookies (CMP)',
		type: 'Preferenční',
		expiration: '1 rok',
	},
];

export function getCookieList(): StorageDefinition[] {
	return SBAZAR_COOKIES;
}

export function getCookieValue(cookieString: string, name: string): string | undefined {
	const value = cookieString
		?.split(';')
		.find((c) => c.includes(`${name}=`))
		?.split('=')[1];
	return value && decodeURIComponent(value);
}

export function combineWithNewCookies(existingCookies: string, newCookies: Record<string, string>) {
	const parsedCookies = existingCookies
		? Object.fromEntries(existingCookies.split(';').map((pair) => pair.trim().split('=')))
		: {};

	const combinedCookies = {
		...parsedCookies,
		...newCookies,
	};

	return Object.entries<string>(combinedCookies)
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join('; ');
}

export class CookieBooleanSignal extends Signal<boolean> {
	#cookieName: string;
	#cookieExpiration: number;
	#defaultValue: boolean;

	constructor(defaultValue: boolean, cookieName: string, cookieExpiration: number = 7 * 24 * 60 * 60 * 1000) {
		super(
			globalThis.document?.cookie.includes(`${cookieName}=`)
				? globalThis.document.cookie.includes(`${cookieName}=true`)
				: defaultValue,
		);
		this.#defaultValue = defaultValue;
		this.#cookieName = cookieName;
		this.#cookieExpiration = cookieExpiration;

		if (globalThis.document) {
			this.subscribe((value) => {
				const expires = new Date();
				expires.setTime(expires.getTime() + this.#cookieExpiration);
				const cookieExists = document.cookie.includes(`${this.#cookieName}=`);
				if (!cookieExists && value === this.#defaultValue) {
					// Do not set cookie if it doesn't exist and value is default
					return;
				}

				if (value === this.#defaultValue) {
					// Remove cookie if value is set to default
					document.cookie = `${this.#cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
				} else {
					// Set cookie if value changes from default
					document.cookie = `${this.#cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
				}
			});
		}
	}
}
